import { UI, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React from 'react';

import {
  TableContent,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useEquitySellAssetController } from '../../../../../hooks/products/EquitySell';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../../Equity/ListLoadingItem/mobile';

import { EquityListItemTypes } from '.';

export const EquityListItemMobile: React.FC<EquityListItemTypes.Props> = ({ offer }) => {
  const id = offer.quote.productId;
  const {
    amountItem,
    priceItem,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    availableAmount,
  } = useEquitySellAssetController(offer);
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderQuantity: amountItem, orderValue: priceItem };

  const { Button, isSelected, forceLoading } = infoGetter(
    'equity',
    completeOffer,
    'productIdNormalized'
  );

  const infos = [
    {
      label: 'Melhor Compra',
      value: <UI.Text color="neutral.dark">{offer.bidPriceNormalized}</UI.Text>,
    },
    {
      label: 'Melhor Venda',
      value: <UI.Text color="neutral.dark">{offer.askPriceNormalized}</UI.Text>,
    },
    {
      label: 'Fechamento',
      value: <UI.Text color="neutral.dark">{offer.closePriceNormalized}</UI.Text>,
    },
  ];

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <AssetTitleWithImage
            image={offer.imageNormalized || ''}
            title={offer.productNameNormalized || ''}
            subtitle={offer.symbolNormalized || ''}
          />
          <AssetValueWithPercentage
            value={offer.lastPriceNormalized}
            percentage={offer.priceChangeTaxValueNormalized}
            iconColor={offer.priceChangeTaxColorNormalized}
            updateDate={offer.tradeDateNormalized}
          />
        </HStack>
        <HStack justifyContent="space-between">
          {infos.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <VStack space={Spacing['inset.quarck']}>
          <UI.Text color="neutral.black" variant="semibold">
            Tipo da ordem
          </UI.Text>
          <VStack
            background={Color.background}
            p={Spacing['inset.nano']}
            borderRadius={BoxRadius.sm}>
            <UI.Text color="neutral.black" py={Spacing['stack.quarck']}>
              LIMITADA
            </UI.Text>
          </VStack>
        </VStack>
        <VStack space={Spacing['stack.quarck']} w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Quantidade
          </UI.Text>
          <UI.InputStepper
            stepperValue={cautionStepper}
            max={availableAmount}
            min={fractionalLotSize}
            name={`equity-amount-order-input${id}`}
            keyboardType="numeric"
            defaultValue={amountItem}
            onChange={handleChangeAmountItem}
            showErrorMessage={false}
            onFocus={() => setIsFocusedAmountInput(true)}
            onBlur={() => setIsFocusedAmountInput(false)}
            {...(isFocusedAmountInput &&
              offer.saleAvailableAmount && {
                helpText: `Quantidade disp.: ${offer.saleAvailableAmount}`,
              })}
            {...(totalPriceIsInvalid && {
              customError: 'Quantidade inválida',
            })}
          />
        </VStack>
        <VStack space={Spacing['stack.quarck']} w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Preço por ativo
          </UI.Text>
          <UI.InputStepper
            stepperValue={0.01}
            max={1000000}
            min={0.01}
            fontSize={Typography['fontsize.md']}
            keyboardType="numeric"
            ignoreStepperDivision
            maskOption="money"
            name={`equity-amount-price${id}`}
            testId={`equity-amount-price${id}`}
            defaultValue={priceItem || 0.01}
            onChange={handleChangePriceItem}
            showErrorMessage={false}
            disabled={isSelected}
            {...((totalPrice === 0 || totalPrice > 1000000) && {
              customError: 'Valor inválido',
            })}
          />
        </VStack>
        <HStack justifyContent="space-between">
          <VStack space={Spacing['stack.quarck']} alignSelf="end">
            <UI.Text color="neutral.black">Total estimado</UI.Text>
            <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']}>
              {Utils.Mask.Money(totalPrice)}
            </UI.Text>
            {totalPriceIsInvalid && (
              <TableContentItemText
                fontSize={Typography['fontsize.xs']}
                color="feedback.error.default">
                Valor máximo atingido
              </TableContentItemText>
            )}
          </VStack>
          <Button
            id={selectedTab.typeGetterId}
            offer={completeOffer}
            isDisabled={totalPriceIsInvalid}
            productContextConstant={selectedTab}
          />
        </HStack>
      </VStack>
    </TableContent>
  );
};
